'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { Region, RegionCode } from 'shared/src/types/region.type';
// type-only: region-ui-content is a server-only module, the type import is erased at build time
import type { RegionUiContent } from '@/lib/ui-content/region-ui-content';
import { countryFlagEmoji } from './country-flag';
import { FiMapPin, FiSearch, FiChevronDown, FiChevronUp, FiNavigation } from 'react-icons/fi';

// What a picked Google place (or the IP-detected location) resolves to.
// regionCode is the INTERNAL code (GB already normalized to UK) or null when
// the country isn't one we serve.
export type PanelResolvedPlace = {
  regionCode: string | null;
  countryIso?: string | null;
  admin1Short?: string | null;
  admin1Long?: string | null;
  city?: string | null;
  lat?: number | null;
  lng?: number | null;
  label?: string | null;
};

type Suggestion = {
  placeId: string;
  mainText: string;
  secondaryText: string;
  types: string[];
};

type DetectedLocation = {
  country: string;
  region: string | null;
  city: string | null;
};

type LocationSearchPanelProps = {
  locale: LocaleDetails;
  available: string[]; // Region enum names, selectable
  comingSoon: string[]; // Region enum names, announced but not live
  selectedCountry: string | null;
  getFullRegionName: (region: string) => string;
  onCountrySelect: (regionName: string) => void;
  onPlaceResolved: (place: PanelResolvedPlace) => void;
  uiContent: RegionUiContent;
};

// Region and RegionCode are parallel enums (same keys) — key-join both ways.
const REGION_NAME_TO_CODE: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  (Object.keys(Region) as (keyof typeof Region)[]).forEach((key) => {
    if (key === 'All') return;
    const code = RegionCode[key as keyof typeof RegionCode];
    if (code) map[Region[key]] = code;
  });
  return map;
})();

const REGION_CODE_TO_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(REGION_NAME_TO_CODE).map(([name, code]) => [code, name]),
);

function newSessionToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function LocationSearchPanel({
  locale,
  available,
  comingSoon,
  selectedCountry,
  getFullRegionName,
  onCountrySelect,
  onPlaceResolved,
  uiContent,
}: LocationSearchPanelProps) {
  const content = uiContent.locationPanel;

  // --- Autocomplete state ---
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  // null = unknown yet; false = API key missing server-side → hide the search
  const [searchConfigured, setSearchConfigured] = useState<boolean | null>(null);
  const sessionTokenRef = useRef<string>(newSessionToken());
  const abortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // --- Detected (IP) location ---
  const [detected, setDetected] = useState<DetectedLocation | null>(null);
  const [isDetecting, setIsDetecting] = useState(true);

  // --- Coming-soon disclosure ---
  const [showComingSoon, setShowComingSoon] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/detect-region');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (typeof data.country === 'string' && data.country) {
          setDetected({
            country: data.country,
            region: typeof data.region === 'string' ? data.region : null,
            city: typeof data.city === 'string' ? data.city : null,
          });
        }
      } catch {
        // Silent — the chip simply doesn't render.
      } finally {
        if (!cancelled) setIsDetecting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced autocomplete against our server proxy.
  useEffect(() => {
    const trimmed = input.trim();
    if (trimmed.length < 3 || searchConfigured === false) {
      setSuggestions([]);
      setIsFetching(false);
      abortRef.current?.abort();
      return;
    }

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsFetching(true);
      try {
        const res = await fetch('/api/places/autocomplete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            input: trimmed,
            sessionToken: sessionTokenRef.current,
            language: locale.languageCode,
          }),
          signal: controller.signal,
        });
        if (res.status === 503) {
          setSearchConfigured(false);
          setSuggestions([]);
          return;
        }
        if (!res.ok) {
          setSuggestions([]);
          return;
        }
        const data = await res.json();
        setSearchConfigured(true);
        const next: Suggestion[] = Array.isArray(data.suggestions) ? data.suggestions : [];
        setSuggestions(next);
        setDropdownOpen(true);
        setHighlighted(next.length > 0 ? 0 : -1);
      } catch (error) {
        if ((error as Error)?.name !== 'AbortError') setSuggestions([]);
      } finally {
        if (abortRef.current === controller) setIsFetching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [input, locale.languageCode, searchConfigured]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const pickSuggestion = useCallback(
    async (suggestion: Suggestion) => {
      setIsResolving(true);
      setDropdownOpen(false);
      try {
        const params = new URLSearchParams({
          placeId: suggestion.placeId,
          sessionToken: sessionTokenRef.current,
          language: locale.languageCode,
        });
        const res = await fetch(`/api/places/details?${params.toString()}`);
        // Details closes the Google billing session — always start a new one.
        sessionTokenRef.current = newSessionToken();
        if (!res.ok) return;
        const data = await res.json();
        const place = data.place;
        if (!place) return;
        setInput(suggestion.mainText);
        onPlaceResolved({
          regionCode: place.regionCode ?? null,
          countryIso: place.countryIso ?? null,
          admin1Short: place.admin1Short ?? null,
          admin1Long: place.admin1Long ?? null,
          city: place.city ?? null,
          lat: place.lat ?? null,
          lng: place.lng ?? null,
          label: place.label ?? suggestion.mainText,
        });
      } catch {
        // Swallow — user can retry or use the country grid.
      } finally {
        setIsResolving(false);
      }
    },
    [locale.languageCode, onPlaceResolved],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!dropdownOpen || suggestions.length === 0) {
      if (event.key === 'Escape') setDropdownOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = suggestions[highlighted] ?? suggestions[0];
      if (target) pickSuggestion(target);
    } else if (event.key === 'Escape') {
      setDropdownOpen(false);
    }
  };

  const detectedRegionName = detected ? REGION_CODE_TO_NAME[detected.country] ?? null : null;
  const detectedIsAvailable = !!detectedRegionName && available.includes(detectedRegionName);
  const detectedLabel = detected?.city || (detectedRegionName ? getFullRegionName(detectedRegionName) : null);

  const handleDetectedClick = () => {
    if (!detected || !detectedIsAvailable) return;
    onPlaceResolved({
      regionCode: detected.country,
      admin1Short: detected.region,
      admin1Long: detected.region,
      city: detected.city,
      label: detectedLabel,
    });
  };

  const orderedAvailable = useMemo(() => {
    // Current site's country first, then detected, then alphabetical.
    const currentName = REGION_CODE_TO_NAME[locale.regionCode] ?? null;
    return [...available].sort((a, b) => {
      const rank = (r: string) => (r === currentName ? 0 : r === detectedRegionName ? 1 : 2);
      return rank(a) - rank(b) || getFullRegionName(a).localeCompare(getFullRegionName(b));
    });
  }, [available, locale.regionCode, detectedRegionName, getFullRegionName]);

  const showSearch = searchConfigured !== false;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header. No mobile top padding to clear a floating back button: the
          region page passes `hideBackButton`, so nothing floats over this
          column, and the padding was ~52px of dead air above the fold. */}
      <div className="px-5 pt-5 pb-3 max-md:px-4 max-md:pt-3 max-md:pb-2">
        <h2 className="text-lg font-bold leading-tight text-gray-900 max-md:text-base dark:text-gray-100">
          {content.title}
        </h2>
        <p className="mt-1 text-xs text-gray-500 max-md:mt-0.5 dark:text-gray-400">{content.subtitle}</p>
      </div>

      {/* Google-powered location search */}
      <div className="px-4 pb-3 max-md:pb-2" ref={containerRef}>
        {showSearch ? (
          <div className="relative">
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                {isFetching || isResolving ? (
                  <div className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-[#00a798] animate-spin" />
                ) : (
                  <FiSearch className="h-4 w-4 text-gray-400 dark:text-gray-500 group-focus-within:text-[#00a798] dark:group-focus-within:text-[#B0E0DC] transition-colors" />
                )}
              </div>
              <input
                ref={inputRef}
                type="text"
                inputMode="search"
                autoComplete="off"
                placeholder={content.searchPlaceholder}
                value={input}
                disabled={isResolving}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (e.target.value.trim().length >= 3) setDropdownOpen(true);
                }}
                onFocus={() => {
                  if (suggestions.length > 0) setDropdownOpen(true);
                }}
                onKeyDown={handleKeyDown}
                className="w-full rounded-full border-0 bg-gray-100 dark:bg-gray-800/80 py-3 max-md:py-2.5 pl-11 pr-4 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a798]/30 dark:focus:ring-[#B0E0DC]/30 focus:bg-white dark:focus:bg-gray-800 transition-all duration-200 shadow-sm disabled:opacity-60"
              />
            </div>

            {/* Suggestions dropdown */}
            {dropdownOpen && input.trim().length >= 3 && (
              <div className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-xl dark:border-gray-700/60 dark:bg-gray-900">
                {suggestions.length > 0 ? (
                  <ul role="listbox">
                    {suggestions.map((suggestion, index) => (
                      <li key={suggestion.placeId} role="option" aria-selected={index === highlighted}>
                        <button
                          type="button"
                          onMouseEnter={() => setHighlighted(index)}
                          onClick={() => pickSuggestion(suggestion)}
                          className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                            index === highlighted ? 'bg-[#00a798]/10 dark:bg-[#00a798]/15' : ''
                          }`}
                        >
                          <FiMapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#00a798]" />
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {suggestion.mainText}
                            </span>
                            {suggestion.secondaryText && (
                              <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                {suggestion.secondaryText}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  !isFetching && (
                    <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{content.noResults}</div>
                  )
                )}
                {/* Required attribution when rendering Places results off-map */}
                <div className="border-t border-gray-100 px-4 py-1.5 text-right text-[10px] tracking-wide text-gray-400 dark:border-gray-800 dark:text-gray-500">
                  Powered by Google
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="rounded-xl bg-amber-50/80 px-4 py-2.5 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
            {content.searchUnavailable}
          </p>
        )}

        {/* Detected location chip */}
        {isDetecting ? (
          <div className="mt-2.5 flex items-center gap-2 px-1 text-xs text-gray-400 max-md:mt-2 dark:text-gray-500">
            <div className="h-3 w-3 rounded-full border-2 border-gray-300 border-t-[#00a798] animate-spin" />
            <span>{content.detecting}</span>
          </div>
        ) : (
          detectedIsAvailable &&
          detectedLabel && (
            <button
              type="button"
              onClick={handleDetectedClick}
              aria-label={`${content.useMyLocation} — ${detectedLabel}`}
              className="group mt-2.5 flex w-full items-center gap-2.5 rounded-xl border border-[#00a798]/25 bg-[#00a798]/5 px-3.5 py-2.5 text-left max-md:mt-2 max-md:py-2 transition-all hover:border-[#00a798]/50 hover:bg-[#00a798]/10 active:scale-[0.99] dark:border-[#00a798]/30 dark:bg-[#00a798]/10 dark:hover:bg-[#00a798]/15"
            >
              <FiNavigation className="h-4 w-4 flex-shrink-0 text-[#00a798]" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {detectedLabel}
                </span>
                <span className="block text-[11px] text-[#00a798] max-md:hidden dark:text-[#B0E0DC]">{content.nearYou}</span>
              </span>
              <span className="text-xs font-medium text-gray-400 group-hover:text-[#00a798] transition-colors">
                {content.useMyLocation}
              </span>
            </button>
          )
        )}
      </div>

      {/* Country grid */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <h3 className="mb-2 px-1 font-ui text-[11px] font-semibold uppercase tracking-wider text-gray-400 max-md:mb-1.5 dark:text-gray-500">
          {content.browseCountries}
        </h3>
        <div className="grid grid-cols-2 gap-2 max-md:gap-1.5">
          {orderedAvailable.map((region) => {
            const code = REGION_NAME_TO_CODE[region] ?? '';
            const isSelected = selectedCountry === region;
            const regionId = `region-button-${region.replace(/\s+/g, '-').toLowerCase()}`;
            return (
              <button
                key={region}
                id={regionId}
                type="button"
                aria-label={getFullRegionName(region)}
                onClick={() => onCountrySelect(region)}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition-all duration-150 active:scale-[0.98] max-md:gap-1.5 max-md:px-2.5 max-md:py-2 ${
                  isSelected
                    ? 'border-[#00a798] bg-[#00a798] text-white shadow-md shadow-[#00a798]/20'
                    : 'border-gray-200/70 bg-white/70 hover:border-[#00a798]/40 hover:bg-[#00a798]/5 dark:border-gray-700/60 dark:bg-gray-800/50 dark:hover:border-[#00a798]/40 dark:hover:bg-[#00a798]/10'
                }`}
              >
                <span className="text-lg leading-none max-md:text-base" aria-hidden>
                  {countryFlagEmoji(code)}
                </span>
                <span
                  className={`min-w-0 truncate text-sm font-semibold ${
                    isSelected ? 'text-white' : 'text-gray-800 dark:text-gray-200'
                  }`}
                >
                  {getFullRegionName(region)}
                </span>
              </button>
            );
          })}
        </div>

        {/* Coming soon — collapsed by default to keep the menu simple */}
        {comingSoon.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowComingSoon((prev) => !prev)}
              className="flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <span>
                {content.comingSoon} ({comingSoon.length})
              </span>
              {showComingSoon ? <FiChevronUp className="h-3 w-3" /> : <FiChevronDown className="h-3 w-3" />}
            </button>
            {showComingSoon && (
              <div className="mt-2 grid grid-cols-2 gap-2 max-md:gap-1.5">
                {comingSoon.map((region) => {
                  const code = REGION_NAME_TO_CODE[region] ?? '';
                  return (
                    <button
                      key={region}
                      type="button"
                      aria-label={`${getFullRegionName(region)} — ${content.comingSoon}`}
                      onClick={() => onCountrySelect(region)}
                      className="flex items-center gap-2 rounded-xl border border-dashed border-amber-300/60 bg-amber-50/40 px-3 py-2.5 text-left opacity-80 max-md:gap-1.5 max-md:px-2.5 max-md:py-2 transition-all hover:opacity-100 dark:border-amber-700/40 dark:bg-amber-950/20"
                    >
                      <span className="text-lg leading-none saturate-50 max-md:text-base" aria-hidden>
                        {countryFlagEmoji(code)}
                      </span>
                      <span className="min-w-0 truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                        {getFullRegionName(region)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
